import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, Lock, Loader2, User } from "lucide-react";
import { http, formatApiError } from "@/api/http";
import { setNativeToken } from "@/lib/native";
import { Button } from "@/components/ui/button";
import AuthLayout from "@/components/AuthLayout";
import { IconField, FormAlert } from "@/components/auth/IconField";
import { SetupInvalidLink } from "@/components/auth/SetupInvalidLink";
import { useSetupToken } from "@/hooks/useSetupToken";

export default function Setup() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { loading, email, fullName: loadedName, tokenError } = useSetupToken(token);

  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setFullName(loadedName); }, [loadedName]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (password.length < 6) return setError(t("setup.passwordTooShort"));
    if (password !== confirmPassword) return setError(t("setup.passwordsDontMatch"));
    setSubmitting(true);
    try {
      const { data } = await http.post("/auth/complete-setup", { token, password, full_name: fullName });
      await setNativeToken(data.token);
      window.location.href = "/";
    } catch (failure) {
      setError(formatApiError(failure, t("setup.createError")));
      setSubmitting(false);
    }
  };

  if (loading) return <div data-testid="setup-loading" className="fixed inset-0 flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-secondary border-t-primary rounded-full animate-spin" />
  </div>;

  if (tokenError) return <SetupInvalidLink reason={tokenError} />;

  return <AuthLayout icon={Sparkles} title={t("setup.title")} subtitle={t("setup.subtitle")}>
    <div className="mb-4 p-3 rounded-lg bg-primary/10 border border-primary/30 text-sm">
      <p className="text-foreground">
        {t("setup.accessingAs")} <strong className="text-primary" data-testid="setup-email">{email}</strong>
      </p>
    </div>
    <div className="mb-4"><FormAlert message={error} testId="setup-error" /></div>
    <form onSubmit={handleSubmit} className="space-y-4">
      <IconField id="name" icon={User} label={t("setup.nameLabel")} data-testid="setup-name-input"
        type="text" autoFocus required placeholder={t("setup.namePlaceholder")}
        value={fullName} onChange={(event) => setFullName(event.target.value)} />
      <IconField id="password" icon={Lock} label={t("setup.passwordLabel")} data-testid="setup-password-input"
        type="password" autoComplete="new-password" required placeholder={t("setup.passwordPlaceholder")}
        value={password} onChange={(event) => setPassword(event.target.value)} />
      <IconField id="confirm" icon={Lock} label={t("common.confirmPassword")} data-testid="setup-confirm-input"
        type="password" autoComplete="new-password" required placeholder={t("setup.confirmPlaceholder")}
        value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
      <Button type="submit" className="w-full h-12 font-medium" disabled={submitting} data-testid="setup-submit-btn">
        {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        {submitting ? t("setup.submitting") : t("setup.submit")}
      </Button>
    </form>
  </AuthLayout>;
}
